<template>
  <v-container fluid class="admin-online-users">
    <NcrmHomePageHeader
      icon="mdi-account-clock-outline"
      :title="$t('admin.onlineUsers.title')"
      :subtitle="$t('admin.onlineUsers.subtitle')"
    >
      <div class="admin-online-users__live">
        <span class="admin-online-users__live-dot" :class="{ 'is-paused': loading }" />
        {{ $t('admin.onlineUsers.live', { seconds: refreshSeconds }) }}
      </div>
    </NcrmHomePageHeader>

    <v-alert v-if="errorMessage" type="error" class="mt-4">
      {{ errorMessage }}
    </v-alert>

    <NcrmHomeKpiCards class="mt-4" :items="kpiItems" />

    <v-card variant="flat" class="admin-online-users__panel mt-4">
      <div class="admin-online-users__panel-header">
        <div>
          <h2>{{ $t('admin.onlineUsers.list.title') }}</h2>
          <p>{{ $t('admin.onlineUsers.list.subtitle') }}</p>
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="rows"
        :loading="loading && !rows.length"
        item-key="userId"
        density="comfortable"
        hide-default-footer
        :items-per-page="-1"
      >
        <template #item.user="{ item }">
          <div class="admin-online-users__user-cell">
            <span class="admin-online-users__status-dot" :class="item.online ? 'is-online' : 'is-idle'" />
            <div class="min-w-0">
              <div class="admin-online-users__user-name">{{ item.name || $t('admin.onlineUsers.unknownUser') }}</div>
              <div class="admin-online-users__user-email">{{ item.email }}</div>
            </div>
          </div>
        </template>

        <template #item.company="{ item }">
          {{ item.companyName || (item.companyId ? `#${item.companyId}` : '—') }}
        </template>

        <template #item.role="{ item }">
          <v-chip color="secondary">{{ item.role || '—' }}</v-chip>
        </template>

        <template #item.lastRoute="{ item }">
          <span class="admin-online-users__route">{{ item.lastRoute || '—' }}</span>
        </template>

        <template #item.status="{ item }">
          <v-chip :color="item.online ? 'success' : 'warning'">
            {{ item.online ? $t('admin.onlineUsers.statuses.online') : $t('admin.onlineUsers.statuses.idle') }}
          </v-chip>
        </template>

        <template #item.lastSeen="{ item }">
          <span :title="formatAbsolute(item.lastSeenAt)">{{ item.relative }}</span>
        </template>

        <template #no-data>
          <v-alert type="info" class="ma-4">
            {{ $t('admin.onlineUsers.empty') }}
          </v-alert>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import NcrmHomeKpiCards from "@/components/ncrm/shared/NcrmHomeKpiCards.vue";
import NcrmHomePageHeader from "@/components/ncrm/shared/NcrmHomePageHeader.vue";
import { formatExternalApiDateTime } from "@/Utile/ncrmExternalApi.mjs";
import { fetchOnlineUsers, formatSecondsAgo } from "./onlineUsers/onlineUsersApi.mjs";

const REFRESH_SECONDS = 30;

export default {
  name: "AdminOnlineUsersHome",
  components: {
    NcrmHomeKpiCards,
    NcrmHomePageHeader,
  },
  data() {
    return {
      loading: false,
      errorMessage: "",
      users: [],
      onlineCount: 0,
      windowMinutes: 60,
      refreshSeconds: REFRESH_SECONDS,
      pollTimer: null,
    };
  },
  computed: {
    rows() {
      return this.users.map((user) => ({
        ...user,
        relative: formatSecondsAgo(user.secondsAgo, this.$t.bind(this)),
      }));
    },
    companiesCount() {
      return new Set(this.users.map((user) => user.companyId).filter(Boolean)).size;
    },
    kpiItems() {
      return [
        { key: "active", label: this.$t("admin.onlineUsers.metrics.activeLastHour"), value: this.users.length, icon: "mdi-account-multiple-outline", color: "primary" },
        { key: "online", label: this.$t("admin.onlineUsers.metrics.onlineNow"), value: this.onlineCount, icon: "mdi-access-point", color: "success" },
        { key: "companies", label: this.$t("admin.onlineUsers.metrics.companies"), value: this.companiesCount, icon: "mdi-domain", color: "info" },
        { key: "window", label: this.$t("admin.onlineUsers.metrics.window"), value: this.$t("admin.onlineUsers.metrics.windowValue", { minutes: this.windowMinutes }), icon: "mdi-clock-outline", color: "secondary" },
      ];
    },
    headers() {
      return [
        { title: this.$t("admin.onlineUsers.table.user"), key: "user", sortable: false },
        { title: this.$t("admin.onlineUsers.table.company"), key: "company", sortable: false },
        { title: this.$t("admin.onlineUsers.table.role"), key: "role", sortable: false },
        { title: this.$t("admin.onlineUsers.table.lastRoute"), key: "lastRoute", sortable: false },
        { title: this.$t("admin.onlineUsers.table.status"), key: "status", sortable: false },
        { title: this.$t("admin.onlineUsers.table.lastSeen"), key: "lastSeen", sortable: false },
      ];
    },
  },
  mounted() {
    this.loadUsers();
    this.pollTimer = setInterval(this.loadUsers, REFRESH_SECONDS * 1000);
  },
  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  methods: {
    async loadUsers() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const { data } = await fetchOnlineUsers(this.windowMinutes);
        this.users = Array.isArray(data?.users) ? data.users : [];
        this.onlineCount = Number(data?.onlineCount) || 0;
        this.windowMinutes = Number(data?.windowMinutes) || this.windowMinutes;
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("admin.onlineUsers.errors.load");
      } finally {
        this.loading = false;
      }
    },
    formatAbsolute(value) {
      return formatExternalApiDateTime(value);
    },
  },
};
</script>

<style scoped>
.admin-online-users {
  background: var(--app-page-background);
  min-height: 100%;
}

.admin-online-users__live {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--app-text-default);
  font-size: 0.82rem;
  opacity: 0.72;
}

.admin-online-users__live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgb(var(--v-theme-success));
  box-shadow: 0 0 0 rgba(var(--v-theme-success), 0.5);
  animation: admin-online-users-pulse 2s infinite;
}

.admin-online-users__live-dot.is-paused {
  animation: none;
  opacity: 0.5;
}

@keyframes admin-online-users-pulse {
  0% { box-shadow: 0 0 0 0 rgba(var(--v-theme-success), 0.45); }
  70% { box-shadow: 0 0 0 6px rgba(var(--v-theme-success), 0); }
  100% { box-shadow: 0 0 0 0 rgba(var(--v-theme-success), 0); }
}

.admin-online-users__panel {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: var(--app-radius-base);
  padding: 20px;
}

.admin-online-users__panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.admin-online-users__panel-header h2 {
  margin: 0;
  color: var(--app-text-strong);
  font-size: 1.05rem;
  font-weight: 700;
}

.admin-online-users__panel-header p {
  margin: 4px 0 0;
  color: var(--app-text-default);
  opacity: 0.68;
  line-height: 1.5;
}

.admin-online-users__user-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.admin-online-users__status-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex: 0 0 auto;
}

.admin-online-users__status-dot.is-online {
  background: rgb(var(--v-theme-success));
}

.admin-online-users__status-dot.is-idle {
  background: rgb(var(--v-theme-warning));
}

.admin-online-users__user-name {
  color: var(--app-text-strong);
  font-weight: 700;
}

.admin-online-users__user-email {
  color: var(--app-text-default);
  font-size: 0.82rem;
  opacity: 0.62;
}

.admin-online-users__route {
  font-family: var(--app-font-family-mono);
  font-size: 0.8rem;
  color: var(--app-text-default);
  opacity: 0.78;
}
</style>
